/**
 * Envelope encryption for stored media.
 *
 * Objects in the bucket are ciphertext. A breach of the storage account — a
 * leaked API token, a misconfigured bucket, a compromised provider — yields
 * bytes nobody can read, because the keys are never in the bucket and are never
 * held by the storage provider.
 *
 * Two layers:
 *
 *   · a *master key*, supplied through the environment and held only by our own
 *     services, which encrypts nothing but other keys;
 *   · a *data key* per stored object: random, used once, and kept beside the
 *     object's row in the database in wrapped form.
 *
 * That split is what makes the master key rotatable. Re-wrapping a few thousand
 * short data keys is a migration; re-encrypting terabytes of media is not. It
 * also bounds the damage from any single key.
 *
 * Everything here uses WebCrypto only, so one implementation runs unchanged in
 * the API, in the media worker, and in the edge worker that serves images.
 *
 * ── Why chunked, rather than one GCM operation over the whole file ──
 *
 * AES-GCM is all-or-nothing: the middle of a message cannot be decrypted
 * without the whole of it. A single-shot format would mean holding an entire
 * 5 GB video in memory to serve it, and would make HTTP range requests — how
 * every browser seeks in a video — impossible to answer.
 *
 * So the plaintext is split into fixed-size chunks, each sealed separately.
 * Fixed size is the point: it turns "which bytes do I need" into arithmetic, so
 * a range request reads only the chunks it actually covers.
 *
 * ── What the additional authenticated data is for ──
 *
 * Sealing chunks independently invites two attacks that authentication alone
 * does not stop, because each chunk is individually valid:
 *
 *   · reordering, or swapping chunks between files encrypted under one key;
 *   · truncation — dropping trailing chunks to silently shorten a file.
 *
 * Each chunk therefore authenticates its own index and whether it is the last,
 * so a moved chunk and a removed tail both fail to open.
 */

const MAGIC = new Uint8Array([0x46, 0x43, 0x45, 0x31]); // "FCE1"

/** Bumped only for a breaking format change; readers reject anything else. */
export const MEDIA_CIPHER_VERSION = 1;

/**
 * Plaintext bytes per chunk.
 *
 * Every chunk costs a 16-byte tag, so smaller chunks mean more overhead; larger
 * ones mean a range request reads more than it needs and each concurrent
 * decrypt holds more memory. At 64 KiB the overhead is 0.02%, and the smallest
 * possible range read costs 64 KiB.
 *
 * Written into the header rather than assumed, so this can change later without
 * stranding anything already stored.
 */
export const DEFAULT_CHUNK_BYTES = 64 * 1024;

const KEY_BYTES = 32; // AES-256
const IV_BYTES = 12; // GCM standard nonce
const TAG_BYTES = 16;
const NONCE_PREFIX_BYTES = 8; // header nonce; the remaining 4 bytes are a counter

export const MEDIA_HEADER_BYTES =
  MAGIC.length + 1 /* version */ + 1 /* flags */ + 4 /* chunk size */ + NONCE_PREFIX_BYTES;

import { bytesToHex } from "./sha256.js";

export class MediaCryptoError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MediaCryptoError";
  }
}

/* ────────────────────────────────── keys ─────────────────────────────────── */

/** A fresh master key, generated once at setup and then stored as a secret. */
export function generateMasterKeyBase64(): string {
  return bytesToBase64(randomBytes(KEY_BYTES));
}

/**
 * Read the master key from its configured form.
 *
 * Length is checked rather than assumed: a truncated or mistyped secret would
 * otherwise surface much later, during a decrypt, where it reads as data
 * corruption rather than as the configuration mistake it is.
 */
export async function importMasterKey(base64: string): Promise<CryptoKey> {
  let raw: Uint8Array;
  try {
    raw = base64ToBytes(base64.trim());
  } catch {
    throw new MediaCryptoError("Master key is not valid base64.");
  }
  if (raw.length !== KEY_BYTES) {
    throw new MediaCryptoError(
      `Master key must be ${String(KEY_BYTES)} bytes (${String(KEY_BYTES * 8)}-bit), got ${String(raw.length)}.`,
    );
  }
  return crypto.subtle.importKey("raw", toBuffer(raw), "AES-GCM", false, ["encrypt", "decrypt"]);
}

/** A single-use key for one stored object. */
export function generateDataKey(): Uint8Array {
  return randomBytes(KEY_BYTES);
}

/**
 * Seal a data key under the master key.
 *
 * The nonce is prepended rather than stored separately: a wrapped key is only
 * ever handled as one opaque value, and splitting it across two columns invites
 * their being paired wrongly.
 */
export async function wrapDataKey(dataKey: Uint8Array, masterKey: CryptoKey): Promise<Uint8Array> {
  if (dataKey.length !== KEY_BYTES) throw new MediaCryptoError("Data key must be 32 bytes.");

  const iv = randomBytes(IV_BYTES);
  const sealed = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv: toBuffer(iv) }, masterKey, toBuffer(dataKey)),
  );
  const out = new Uint8Array(iv.length + sealed.length);
  out.set(iv, 0);
  out.set(sealed, iv.length);
  return out;
}

export async function unwrapDataKey(wrapped: Uint8Array, masterKey: CryptoKey): Promise<Uint8Array> {
  if (wrapped.length !== IV_BYTES + KEY_BYTES + TAG_BYTES) {
    throw new MediaCryptoError("Wrapped data key has the wrong length.");
  }
  const iv = wrapped.subarray(0, IV_BYTES);
  const body = wrapped.subarray(IV_BYTES);
  try {
    return new Uint8Array(
      await crypto.subtle.decrypt({ name: "AES-GCM", iv: toBuffer(iv) }, masterKey, toBuffer(body)),
    );
  } catch {
    // A wrong master key and a tampered value are indistinguishable, and should
    // be: telling them apart would confirm which keys are correct.
    throw new MediaCryptoError("Could not unwrap the data key.");
  }
}

async function importDataKey(dataKey: Uint8Array): Promise<CryptoKey> {
  if (dataKey.length !== KEY_BYTES) throw new MediaCryptoError("Data key must be 32 bytes.");
  return crypto.subtle.importKey("raw", toBuffer(dataKey), "AES-GCM", false, ["encrypt", "decrypt"]);
}

/* ────────────────────────────────── framing ──────────────────────────────── */

interface Header {
  chunkBytes: number;
  noncePrefix: Uint8Array;
}

function writeHeader(chunkBytes: number, noncePrefix: Uint8Array): Uint8Array {
  const header = new Uint8Array(MEDIA_HEADER_BYTES);
  header.set(MAGIC, 0);
  header[MAGIC.length] = MEDIA_CIPHER_VERSION;
  header[MAGIC.length + 1] = 0; // flags, reserved
  new DataView(header.buffer).setUint32(MAGIC.length + 2, chunkBytes, false);
  header.set(noncePrefix, MAGIC.length + 6);
  return header;
}

function readHeader(input: Uint8Array): Header {
  if (input.length < MEDIA_HEADER_BYTES) {
    throw new MediaCryptoError("Ciphertext is too short to hold a header.");
  }
  for (let i = 0; i < MAGIC.length; i++) {
    if (input[i] !== MAGIC[i]) throw new MediaCryptoError("Not an encrypted media object.");
  }
  const version = input[MAGIC.length];
  if (version !== MEDIA_CIPHER_VERSION) {
    throw new MediaCryptoError(`Unsupported cipher version ${String(version)}.`);
  }
  const view = new DataView(input.buffer, input.byteOffset, input.byteLength);
  const chunkBytes = view.getUint32(MAGIC.length + 2, false);
  if (chunkBytes === 0) throw new MediaCryptoError("Header declares a zero chunk size.");

  return { chunkBytes, noncePrefix: input.subarray(MAGIC.length + 6, MEDIA_HEADER_BYTES) };
}

/** IV = 8 bytes fixed per object, then the chunk index. Never repeats per key. */
function ivFor(noncePrefix: Uint8Array, index: number): Uint8Array {
  const iv = new Uint8Array(IV_BYTES);
  iv.set(noncePrefix, 0);
  new DataView(iv.buffer).setUint32(NONCE_PREFIX_BYTES, index, false);
  return iv;
}

/** Binds a chunk to its position, and to being (or not being) the last one. */
function aadFor(index: number, isFinal: boolean): Uint8Array {
  const aad = new Uint8Array(6);
  aad[0] = MEDIA_CIPHER_VERSION;
  new DataView(aad.buffer).setUint32(1, index, false);
  aad[5] = isFinal ? 1 : 0;
  return aad;
}

/* ────────────────────────────────── sizing ───────────────────────────────── */

/**
 * Ciphertext length for a given plaintext length.
 *
 * Needed before any bytes exist: an upload must declare its size, and the quota
 * reservation is taken against what will actually be stored.
 */
export function encryptedLength(plaintextBytes: number, chunkBytes = DEFAULT_CHUNK_BYTES): number {
  if (plaintextBytes < 0) throw new MediaCryptoError("Length cannot be negative.");
  // An empty object is still one sealed chunk, so its length stays unambiguous
  // and truncating it to nothing is still detected.
  const chunks = plaintextBytes === 0 ? 1 : Math.ceil(plaintextBytes / chunkBytes);
  return MEDIA_HEADER_BYTES + plaintextBytes + chunks * TAG_BYTES;
}

/** The inverse, for reporting a stored object's real size. */
export function plaintextLength(ciphertextBytes: number, chunkBytes = DEFAULT_CHUNK_BYTES): number {
  const body = ciphertextBytes - MEDIA_HEADER_BYTES;
  if (body < 0) throw new MediaCryptoError("Ciphertext is too short to hold a header.");

  const sealedChunk = chunkBytes + TAG_BYTES;
  const whole = Math.floor(body / sealedChunk);
  const remainder = body % sealedChunk;
  if (remainder === 0) return whole * chunkBytes;
  if (remainder < TAG_BYTES) throw new MediaCryptoError("Ciphertext length is not a valid framing.");
  return whole * chunkBytes + (remainder - TAG_BYTES);
}

/* ───────────────────────────────── one-shot ──────────────────────────────── */

export async function encrypt(
  plaintext: Uint8Array,
  dataKey: Uint8Array,
  chunkBytes = DEFAULT_CHUNK_BYTES,
): Promise<Uint8Array> {
  const key = await importDataKey(dataKey);
  const noncePrefix = randomBytes(NONCE_PREFIX_BYTES);
  const header = writeHeader(chunkBytes, noncePrefix);

  const out = new Uint8Array(encryptedLength(plaintext.length, chunkBytes));
  out.set(header, 0);

  const total = plaintext.length === 0 ? 1 : Math.ceil(plaintext.length / chunkBytes);
  let offset = header.length;

  for (let index = 0; index < total; index++) {
    const slice = plaintext.subarray(
      index * chunkBytes,
      Math.min((index + 1) * chunkBytes, plaintext.length),
    );
    const sealed = new Uint8Array(
      await crypto.subtle.encrypt(
        {
          name: "AES-GCM",
          iv: ivFor(noncePrefix, index) as BufferSource,
          additionalData: aadFor(index, index === total - 1) as BufferSource,
          // As in `decrypt`: the view, not a clone of it.
        },
        key,
        slice as BufferSource,
      ),
    );
    out.set(sealed, offset);
    offset += sealed.length;
  }

  return out;
}

export async function decrypt(ciphertext: Uint8Array, dataKey: Uint8Array): Promise<Uint8Array> {
  const { chunkBytes, noncePrefix } = readHeader(ciphertext);
  const key = await importDataKey(dataKey);

  const body = ciphertext.subarray(MEDIA_HEADER_BYTES);
  const sealedChunk = chunkBytes + TAG_BYTES;
  const total = Math.max(1, Math.ceil(body.length / sealedChunk));

  /*
   * Written straight into the finished buffer.
   *
   * Collecting the chunks in an array and joining them afterwards holds the
   * whole photograph twice at the moment of the join — and a phone opening a
   * 48 MB video would need 96 MB for a few milliseconds, which is exactly when
   * it gets killed. The length is known in advance, so there is no reason to
   * discover it by accumulating.
   */
  const out = new Uint8Array(plaintextLength(ciphertext.length, chunkBytes));
  let offset = 0;

  for (let index = 0; index < total; index++) {
    const slice = body.subarray(index * sealedChunk, Math.min((index + 1) * sealedChunk, body.length));
    if (slice.length < TAG_BYTES) throw new MediaCryptoError("Truncated chunk.");
    try {
      const plain = new Uint8Array(
        await crypto.subtle.decrypt(
          {
            name: "AES-GCM",
            iv: ivFor(noncePrefix, index) as BufferSource,
            additionalData: aadFor(index, index === total - 1) as BufferSource,
          },
          key,
          // The view itself, not a copy of it. `toBuffer` would clone every
          // chunk, which on a large file is hundreds of pointless allocations
          // and a second pass over every byte.
          slice as BufferSource,
        ),
      );
      out.set(plain, offset);
      offset += plain.length;
    } catch {
      throw new MediaCryptoError(`Chunk ${String(index)} failed authentication.`);
    }
  }

  return out;
}

/* ───────────────────────────── range addressing ──────────────────────────── */

export interface ChunkSpan {
  /** First chunk overlapping the request. */
  firstIndex: number;
  /** Last chunk overlapping it, inclusive. */
  lastIndex: number;
  /** Offset of the sealed region to read from storage. */
  byteStart: number;
  /** Inclusive end offset of that region. */
  byteEnd: number;
  /** Where the wanted bytes begin once those chunks are decrypted. */
  offsetInFirstChunk: number;
}

/**
 * Which stored bytes must be fetched to answer a plaintext range.
 *
 * This is what keeps seeking in a video possible: a player asking for bytes
 * 900 MB in fetches a chunk or two, not the whole file.
 */
export function chunkSpanForRange(
  start: number,
  endInclusive: number,
  chunkBytes = DEFAULT_CHUNK_BYTES,
): ChunkSpan {
  if (start < 0 || endInclusive < start) throw new MediaCryptoError("Invalid range.");

  const sealedChunk = chunkBytes + TAG_BYTES;
  const firstIndex = Math.floor(start / chunkBytes);
  const lastIndex = Math.floor(endInclusive / chunkBytes);

  return {
    firstIndex,
    lastIndex,
    byteStart: MEDIA_HEADER_BYTES + firstIndex * sealedChunk,
    byteEnd: MEDIA_HEADER_BYTES + (lastIndex + 1) * sealedChunk - 1,
    offsetInFirstChunk: start - firstIndex * chunkBytes,
  };
}

/* ─────────────────────────── deduplication tags ──────────────────────────── */

/**
 * A group-scoped identifier for a file's contents.
 *
 * Deduplication needs to answer "have we already got these bytes", and the
 * obvious answer — the SHA-256 of the plaintext — is one the server must never
 * be told. That value is a fingerprint: anyone holding the database can take a
 * photograph they already have, hash it, and learn whether it is in here. It
 * costs nothing to compute and it works against every account at once, which is
 * precisely the question encryption exists to make unanswerable. The bytes are
 * unreadable and the answer leaks anyway.
 *
 * Keying the hash with the group key fixes it. Inside a group the tag is stable,
 * so two copies of the same photograph still collapse into one stored object —
 * and every member of that group can see the file regardless, so nothing is
 * disclosed that was not already shared. Across groups the same photograph
 * produces two unrelated tags, so nothing links them and nothing can be tested
 * for without first holding the key.
 *
 * The cost is honest and worth stating: the same file in two groups is stored
 * twice. Global deduplication and end-to-end encryption cannot both be had, and
 * of the two only one was ever promised.
 */
export async function contentTag(sha256Hex: string, groupKey: Uint8Array): Promise<string> {
  if (!/^[a-f0-9]{64}$/.test(sha256Hex)) {
    throw new MediaCryptoError("A content tag needs a lowercase hex SHA-256.");
  }

  const key = await crypto.subtle.importKey(
    "raw",
    toBuffer(groupKey),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );

  /*
   * The digest is hashed, not the file. The plaintext hash is already a
   * complete summary of the contents, and a second pass over gigabytes to
   * arrive at the same distinctions would buy nothing.
   */
  const tag = await crypto.subtle.sign("HMAC", key, toBuffer(hexToBytes(sha256Hex)));
  return bytesToHex(new Uint8Array(tag));
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let index = 0; index < out.length; index += 1) {
    out[index] = Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16);
  }
  return out;
}

/* ──────────────────────────────── utilities ──────────────────────────────── */

function randomBytes(length: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length));
}

/**
 * WebCrypto wants an ArrayBuffer, and a Uint8Array view may cover only part of
 * a larger one — handing over `.buffer` directly would silently include its
 * neighbours.
 */
function toBuffer(view: Uint8Array): ArrayBuffer {
  return view.byteOffset === 0 && view.byteLength === view.buffer.byteLength
    ? (view.buffer as ArrayBuffer)
    : (view.slice().buffer as ArrayBuffer);
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}
